// Hook React Query per operazioni CRUD sui ticket
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { Ticket, TicketStatus, Paginated } from '@fieldcore/types'
import api from '../lib/api'

// ===== CHIAVI QUERY =====
export const ticketKeys = {
  all: ['tickets'] as const,
  lists: () => [...ticketKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...ticketKeys.lists(), filters] as const,
  details: () => [...ticketKeys.all, 'detail'] as const,
  detail: (id: string) => [...ticketKeys.details(), id] as const,
}

export interface TicketFilters {
  page?: number
  limit?: number
  status?: TicketStatus
  priority?: string
  technicianId?: string
  companyId?: string
  search?: string
  from?: string
  to?: string
  excludeCompleted?: boolean
}

// ===== LISTA TICKET =====
export function useTicketsList(filters: TicketFilters = {}) {
  const { excludeCompleted, ...apiFilters } = filters
  // Se excludeCompleted=true, non passiamo status (backend non filtra) ma filtriamo lato client
  return useQuery({
    queryKey: ticketKeys.list(filters as Record<string, unknown>),
    queryFn: async () => {
      const response = await api.get<Paginated<Ticket>>('/tickets', { params: apiFilters })
      const data = response.data
      if (excludeCompleted && data?.items) {
        return {
          ...data,
          items: data.items.filter((t) => !['completed', 'cancelled'].includes(t.status)),
        }
      }
      return data
    },
  })
}

// ===== DETTAGLIO TICKET =====
export function useTicket(id: string) {
  return useQuery({
    queryKey: ticketKeys.detail(id),
    queryFn: async () => {
      const response = await api.get<Ticket>(`/tickets/${id}`)
      return response.data
    },
    enabled: Boolean(id),
  })
}

// ===== CREA TICKET =====
export function useCreateTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<Ticket>) => {
      const response = await api.post<Ticket>('/tickets', data)
      return response.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ticketKeys.lists() })
    },
  })
}

// ===== AGGIORNA TICKET (status) =====
// Il backend espone solo PATCH /tickets/:id/status — aggiorniamo solo lo status
export function useUpdateTicket(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<Ticket>) => {
      if (data.status) {
        const response = await api.patch<Ticket>(`/tickets/${id}/status`, { status: data.status })
        return response.data
      }
      // Per altri campi non c'è un endpoint dedicato — restituisce i dati attuali dalla cache
      return qc.getQueryData<Ticket>(ticketKeys.detail(id)) as Ticket
    },
    onSuccess: (updated) => {
      if (updated) qc.setQueryData(ticketKeys.detail(id), updated)
      void qc.invalidateQueries({ queryKey: ticketKeys.lists() })
    },
  })
}

// ===== ELIMINA TICKET =====
export function useDeleteTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/tickets/${id}`)
      return id
    },
    onSuccess: (id) => {
      qc.removeQueries({ queryKey: ticketKeys.detail(id) })
      void qc.invalidateQueries({ queryKey: ticketKeys.lists() })
    },
  })
}

// ===== ASSEGNA TICKET =====
export function useAssignTicket(ticketId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (technicianId: string) => {
      const response = await api.post<Ticket>(`/tickets/${ticketId}/assign`, { technicianId })
      return response.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) })
      void qc.invalidateQueries({ queryKey: ticketKeys.lists() })
    },
  })
}
